import { computed, DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CheckChangesService, DialogService, PopoverResult, PopoverService, ToastService } from '@h5s/design-system';
import { BroadcastChannelService } from '@h5s/util-broadcast';
import { ISOStringToDateTime } from '@h5s/util-date';
import { ActionType } from '@h5s/util-overlay';
import { PwaUpdateService } from '@h5s/util-pwa';
import { isNavigationReload } from '@h5s/util-routing';
import { idleAndDeadlineReached, isDeadlineReached, isTabActive$, resettableTimer } from '@h5s/util-rxjs';
import { StorageService } from '@h5s/util-storage';
import { EMPTY, exhaustMap, filter, finalize, Observable, switchMap, take, tap, timer } from 'rxjs';
import { APP_UPDATED_EVENT } from './events/app-updated-event';
import { REQUIRED_UPDATE_EVENT } from './events/required-update-event';
import { TAB_ACTIVE_EVENT } from './events/tab-active-event';
import { TAB_ACTIVE_REQUEST_EVENT } from './events/tab-active-request-event';
import { TAB_ACTIVE_RESPONSE_EVENT } from './events/tab-active-response-event';
import { FeatureUpdateNotificationComponent } from './feature-update-notification/feature-update-notification.component';
import { UpdateNotificationPopoverParams } from './feature-update-notification/models/update-notification-popover-params';
import { FEATURE_UPDATE_NOTIFICATION_TRANSLATIONS } from './feature-update-notification/translations/feature-update-notification.translations';
import { reloadButtonId } from './feature-update-notification/feature-update-notification.component';

/** Tempo di inattività dell'utente in millisecondi prima di considerare la sessione "idle". */
const IDLE_AFTER_MS = 10 * 60 * 1000; // 10 minuti

/**
 * Intervallo in millisecondi per l'invio dell'heartbeat che segnala l'attività di una tab.
 * Deve essere << IDLE_AFTER_MS.
 */
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 1 minuto

/** Intervallo in millisecondi per il controllo periodico del raggiungimento della scadenza dell'aggiornamento. */
const CHECK_DEADLINE_INTERVAL_MS = 5 * 60 * 1000; // 5 minuti

/**
 * Gestisce il ciclo di vita delle notifiche di aggiornamento della PWA.
 * Si occupa di:
 * 1.  Notificare l'utente quando una nuova versione è disponibile tramite un popover non bloccante.
 * 2.  Mostrare un messaggio di successo dopo l'aggiornamento.
 * 3.  Forzare l'aggiornamento con una dialog bloccante quando scade la deadline e l'utente è inattivo.
 * 4.  Sincronizzare l'aggiornamento tra più tab aperte della stessa applicazione.
 */
@Service()
export class FeatureUpdateNotificationService {
  private readonly popoverService = inject(PopoverService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly pwaUpdateService = inject(PwaUpdateService);
  private readonly storageService = inject(StorageService);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly dialogService = inject(DialogService);
  private readonly checkChangesService = inject(CheckChangesService);

  private readonly translation = FEATURE_UPDATE_NOTIFICATION_TRANSLATIONS;

  /** Chiave utilizzata nel Session Storage per tracciare la versione a cui si sta aggiornando. */
  private readonly updatedToVersionKey = 'APP_UPDATED_TO_VERSION';

  /** Flag per evitare l'apertura di più popover contemporaneamente. */
  private popoverIsOpen = false;

  /** Flag per evitare l'apertura di più dialog di aggiornamento obbligatorio. */
  private requiredUpdateDialogIsOpen = false;

  /** Signal che indica se la tab corrente è attiva (visibile e con focus). */
  private readonly isTabActive = toSignal(isTabActive$, { initialValue: true });

  /**
   * Signal che espone la nuova versione disponibile, derivato dal `PwaUpdateService`.
   * È `null` se non ci sono aggiornamenti.
   */
  public readonly newVersion = computed(
    () => this.pwaUpdateService.updateAvailable()?.latestVersion.appData.version ?? null,
  );

  /** Observable che emette solo quando una nuova versione (`newVersion`) diventa disponibile (non è `null`). */
  private readonly onNewVersionAvailable$ = toObservable(this.newVersion).pipe(
    filter((newVersion) => newVersion !== null),
  );

  /** Signal che espone la data di scadenza per l'aggiornamento obbligatorio. */
  private readonly updateDeadline = computed(() => {
    const deadline = this.pwaUpdateService.updateAvailable()?.latestVersion.appData.updateDeadlineUTC ?? null;

    if (deadline) {
      return ISOStringToDateTime(deadline);
    }

    return null;
  });

  /** Observable che emette solo quando una deadline di aggiornamento è definita (non è `null`). */
  private readonly onUpdateDeadline$ = toObservable(this.updateDeadline).pipe(filter((deadline) => deadline !== null));

  /**
   * Inizializza tutti i listener per il ciclo di vita degli aggiornamenti della PWA.
   * Questo metodo è il punto di ingresso principale del servizio.
   */
  public initShowUpdateNotificationListeners(): void {
    // 1. Controlla se la pagina è stata appena ricaricata per un aggiornamento.
    // In tal caso, mostra un toast di successo e notifica le altre tab.
    if (this.storageService.getElementFromSessionStorage<string>(this.updatedToVersionKey)) {
      this.toastService.showSuccess(this.translation.appUpdatedMessage);
      this.storageService.removeElementFromSessionStorage(this.updatedToVersionKey);

      if (isNavigationReload()) {
        this.broadcastChannelService.publish(APP_UPDATED_EVENT);
      }
    }

    // 2. Si mette in ascolto per nuove versioni disponibili.
    // Quando ne arriva una, salva la versione nel session storage e apre il popover di notifica.
    // `exhaustMap` previene l'apertura di nuovi popover se uno è già in gestione.
    this.onNewVersionAvailable$
      .pipe(
        tap((newVersion) => this.storageService.setElementInSessionStorage(this.updatedToVersionKey, newVersion)),
        exhaustMap((newVersion) => this.openUpdateNotificationPopover({ newVersion })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // 3. Si mette in ascolto per la deadline dell'aggiornamento.
    // Quando la deadline viene raggiunta e l'utente è inattivo,
    // mostra una dialog bloccante per forzare l'aggiornamento.
    this.onUpdateDeadline$
      .pipe(
        switchMap((deadline) => idleAndDeadlineReached(IDLE_AFTER_MS, deadline, CHECK_DEADLINE_INTERVAL_MS)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.showRequiredUpdateDialog());

    // 4. Meccanismo di heartbeat per comunicare che questa tab è attiva.
    // Se la tab è attiva, emette un evento `TAB_ACTIVE_EVENT` a intervalli regolari.
    // Questo serve a impedire che altre tab inattive forzino un aggiornamento.
    isTabActive$
      .pipe(
        switchMap((isActive) => (isActive ? timer(0, HEARTBEAT_INTERVAL_MS) : EMPTY)),
        tap(() => this.broadcastChannelService.publish(TAB_ACTIVE_EVENT)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // 5. Logica per l'aggiornamento forzato quando tutte le tab sono inattive.
    // Quando la deadline è raggiunta e questa tab diventa inattiva, invia una richiesta
    // (`TAB_ACTIVE_REQUEST_EVENT`) per verificare se altre tab sono attive. Se nessuna
    // tab risponde entro `IDLE_AFTER_MS`, l'app viene considerata inattiva e si mostra la dialog.
    this.onUpdateDeadline$
      .pipe(
        switchMap((deadline) => isDeadlineReached(deadline, CHECK_DEADLINE_INTERVAL_MS)),
        filter((reached) => reached === true),
        take(1),
        switchMap(() => isTabActive$),
        filter((isActive) => isActive === false),
        tap(() => this.broadcastChannelService.publish(TAB_ACTIVE_REQUEST_EVENT)),
        switchMap(() =>
          resettableTimer(
            IDLE_AFTER_MS,
            this.broadcastChannelService.ofTypes(TAB_ACTIVE_RESPONSE_EVENT, TAB_ACTIVE_EVENT),
          ),
        ),
        filter((reached) => reached === true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.showRequiredUpdateDialog());

    // 6. Risponde alle richieste di stato (`TAB_ACTIVE_REQUEST_EVENT`) da altre tab.
    // Se questa tab è attiva, emette un evento di risposta per segnalare la sua presenza.
    this.broadcastChannelService
      .ofFiltered(TAB_ACTIVE_REQUEST_EVENT, () => this.isTabActive())
      .pipe(
        tap(() => this.broadcastChannelService.publish(TAB_ACTIVE_RESPONSE_EVENT)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // 7. Si mette in ascolto di messaggi da altre tab.
    // Se un'altra tab esegue l'aggiornamento, se non ha modifiche in pending,
    // anche questa tab si aggiornerà per mantenere la consistenza tra le sessioni aperte.
    this.broadcastChannelService
      .ofFiltered(APP_UPDATED_EVENT, () => !!this.newVersion() && !this.checkChangesService.hasPendingChanges())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateApp(false));

    // 8. Si mette in ascolto di messaggi da altre tab che hanno già mostrato la dialog
    // di aggiornamento obbligatorio, per sincronizzare lo stato su tutte le istanze.
    this.broadcastChannelService
      .ofType(REQUIRED_UPDATE_EVENT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showRequiredUpdateDialog());
  }

  /**
   * Metodo pubblico per mostrare manualmente la notifica di aggiornamento.
   * Utile per essere richiamato da un'azione dell'utente (es. un pulsante nella UI).
   */
  public showUpdateNotification(): void {
    const newVersion = this.newVersion();

    if (!newVersion) {
      this.toastService.showInfo(this.translation.noNewVersionAvailableMessage);
      return;
    }

    this.openUpdateNotificationPopover({ newVersion }).pipe(take(1)).subscribe();
  }

  /**
   * Apre il popover di notifica per un nuovo aggiornamento.
   * Se il popover è già aperto, l'operazione viene ignorata per evitare duplicati.
   * @param params I parametri per il popover, che includono la nuova versione da mostrare.
   * @returns Un `Observable` che si completa alla chiusura del popover.
   */
  public openUpdateNotificationPopover(params: UpdateNotificationPopoverParams): Observable<PopoverResult<undefined>> {
    if (this.popoverIsOpen) {
      return EMPTY;
    }

    this.popoverIsOpen = true;

    return this.popoverService
      .show<undefined>(
        { x: 0, y: 0 },
        {
          title: this.translation.newUpdateAvailableTitle,
          component: FeatureUpdateNotificationComponent,
          isPermanent: true,
          isDraggable: false,
          keepOnNavigation: true,
          hideCloseButton: false,
          position: 'bottom-left',
          confirmButtonText: this.translation.reloadNowButton,
          confirmButtonId: reloadButtonId,
          confirmButtonStyle: 'primary',
          positionOffset: {
            left: '75px',
          },
          locals: params,
        },
      )
      .pipe(finalize(() => (this.popoverIsOpen = false)));
  }

  /**
   * Esegue l'aggiornamento dell'applicazione.
   * Salva la versione target nel session storage (per mostrare il toast di successo al riavvio),
   * notifica opzionalmente le altre tab e ricarica la pagina.
   * @param publishEvent Se `true`, invia un messaggio sul broadcast channel per notificare le altre tab.
   */
  public updateApp(publishEvent = true): void {
    this.storageService.setElementInSessionStorage(this.updatedToVersionKey, this.newVersion());
    if (publishEvent) this.broadcastChannelService.publish(APP_UPDATED_EVENT);
    this.pwaUpdateService.reloadApp();
  }

  /**
   * Mostra una dialog modale e bloccante che informa l'utente della necessità
   * di un aggiornamento obbligatorio e lo forza a ricaricare la pagina.
   */
  private showRequiredUpdateDialog(): void {
    if (this.requiredUpdateDialogIsOpen) return;
    this.requiredUpdateDialogIsOpen = true;

    // Notifica le altre tab di mostrare la dialog di aggiornamento obbligatorio.
    this.broadcastChannelService.publish(REQUIRED_UPDATE_EVENT);

    this.dialogService
      .showAlert({
        title: this.translation.requiredUpdateAvailableTitle,
        content: this.translation.sessionOutdatedMessage,
        confirmButtonText: this.translation.reloadAndContinueButton,
        disableCloseOnOverlayClick: true,
        disableCloseOnEsc: true,
      })
      .pipe(
        take(1),
        filter(({ actionType }) => actionType === ActionType.Confirm),
        finalize(() => (this.requiredUpdateDialogIsOpen = false)),
      )
      .subscribe(() => this.updateApp());
  }

  /**
   * Verifica se la dialog di aggiornamento obbligatorio è attualmente visibile.
   *
   * Questo metodo espone lo stato interno del servizio e viene utilizzato, ad esempio,
   * dalla guardia `updateRequiredGuard` per impedire la navigazione quando un
   * aggiornamento forzato è in attesa di essere confermato dall'utente.
   *
   * @returns `true` se la dialog di aggiornamento obbligatorio è aperta, altrimenti `false`.
   */
  public updateIsRequired(): boolean {
    return this.requiredUpdateDialogIsOpen;
  }
}
